export type Lang = 'en' | 'fr' | 'ru'

const translations: Record<Lang, Record<string, string>> = {
  en: {
    // Nav / common
    events: 'Events', endpoints: 'Endpoints', analytics: 'Analytics',
    apiKeys: 'API Keys', settings: 'Settings', help: 'Help',
    openMenu: 'Open menu', closeMenu: 'Close menu',
    signIn: 'Sign in', signUp: 'Create account', signOut: 'Sign out',
    email: 'Email', password: 'Password', name: 'Name',
    newEndpoint: 'New Endpoint', createEndpoint: 'Create Endpoint',
    search: 'Search body...', allStatuses: 'All statuses',
    noWebhooks: 'No webhooks received yet',
    noEndpoints: 'Create your first endpoint',
    replay: 'Replay', replaySent: 'Replay sent', replayFailed: 'Replay failed', page: 'Page',
    stream: 'Stream', searchPlaceholder: 'Filter by id, type, body…',
    errorsOnly: 'Errors only', time: 'Time', event: 'Event', endpoint: 'Endpoint',
    streaming: 'streaming', loaded: 'loaded', errors: 'errors',
    firing: 'firing', armed: 'armed',
    payload: 'Payload', delivery: 'Delivery',
    signatureVerified: 'Signature verified', signatureInvalid: 'Signature invalid',
    replayDescription: 'Resend this event to its original target.',
    holdToReplayHint: 'Press and hold · prevents accidental resends',
    metadata: 'Metadata',
    noHeaders: 'No headers captured.',
    noDeliveryData: 'No delivery timing recorded for this event.',
    resizeInspector: 'Resize inspector',
    ingest: 'Ingest', validate: 'Validate', forward: 'Forward',
    totalDeliveryTime: 'Total time from ingest to response:',
    deliveryEstimateNote: 'Only the total round-trip is measured today; per-stage timing will come from the proxy attempt log.',
    statusCodeLabel: 'Response status',
    responseLabel: 'Response body',
    responseTruncated: 'truncated at 64 KB',
    noHttpResponse: 'No HTTP response (network error).',
    clickToActivate: 'Click to activate', clickToDeactivate: 'Click to deactivate',
    usageUnavailable: 'Usage temporarily unavailable',
    delete: 'Delete', cancel: 'Cancel',
    retry: 'Retry',
    couldntLoadEvents: "Couldn't load the stream.",
    couldntLoadAlerts: "Couldn't load alert rules.",
    checkConnection: 'Check your connection and try again.',
    filterByEndpoint: 'Filter by endpoint',
    deleteAlertQ: 'Delete this alert?',
    deleteAlertDesc: "This rule will stop monitoring and be removed. This can't be undone.",
    alertDeleteFailed: 'Could not delete the alert.',
    alertToggleFailed: 'Could not update the alert.',
    live: 'Live', disconnected: 'Disconnected',
    request: 'Request', response: 'Response', headers: 'Headers', body: 'Body',
    totalEvents: 'Total Events', successRate: 'Success Rate',
    copy: 'Copy', copied: 'Copied', copyProxyUrl: 'Copy proxy URL',
    loading: 'Loading...',
    off: 'Off', unreadCount: '{n} unread', language: 'Language', navigation: 'Navigation',
    confirm: 'Confirm', typeToConfirm: 'Type {x} to confirm.', toggleLineWrap: 'Toggle line wrap',
    done: 'Done',
    linkExpired: 'Invalid or expired link. Please log in again.',
    missingExchangeCode: 'Missing exchange code',
    errorBoundaryTitle: 'Something went wrong',
    errorBoundaryBody: 'The page hit an unexpected error. You can retry or reload the app.',
    reloadApp: 'Reload app',

    // Login / Signup
    signInTitle: 'Sign in to your account',
    signUpTitle: 'Create your account',
    forgotPassword: 'Forgot password?',
    forgotPasswordTitle: 'Reset your password',
    forgotPasswordDesc: 'Enter your email and we will send you a reset link.',
    resetLinkSent: 'If an account exists for that email, a reset link is on its way.',
    sendResetLink: 'Send reset link',
    resetPasswordTitle: 'Choose a new password',
    newPassword: 'New password',
    resetPassword: 'Reset password',
    resetSuccess: 'Password updated. You can now sign in.',
    resetFailed: 'This reset link is invalid or has expired. Request a new one.',
    resetLinkInvalid: 'Missing or invalid reset link.',
    backToLogin: 'Back to login',
    joiningWorkspace: "You've been invited to join a workspace. Create your account to accept.",
    noAccount: 'No account?',
    hasAccount: 'Already have an account?',
    invalidCredentials: 'Invalid email or password',
    signupFailed: 'Sign-up failed. Please try again.',
    emailAlreadyTaken: 'This email is already linked to an account.',
    removeMember: 'Remove member',
    removeMemberConfirm: 'They will lose access to this workspace.',
    leaveWorkspace: 'Leave workspace',
    leaveWorkspaceConfirm: 'You will lose access to this workspace until you are invited again.',
    memberRemoveFailed: 'Could not remove the member.',
    leaveFailed: 'Could not leave the workspace.',
    remove: 'Remove',
    signupValidationError: 'Please check your details and try again.',
    disposableEmailBlocked: 'Disposable email addresses are not supported. Please use your real address.',
    passwordMinLength: 'Password must be at least 8 characters',
    signingIn: 'Signing in...',
    creatingAccount: 'Creating account...',
    sign_in_with_github: 'Sign in with GitHub',
    or: 'or',

    // OAuth (Hydra login/consent)
    oauthConnecting: 'Connecting your account…',
    oauthFailed: 'Authorization failed. Please try again.',
    oauthMissingChallenge: 'Invalid authorization request.',

    // Events page
    noWebhooksTitle: 'Waiting for your first webhook',
    noWebhooksDesc: 'Point any webhook source at your endpoint URL, or try it right now with the command below.',
    sendTestCommand: 'Send a test event',
    firstEventRealtime: 'The first event arrives here in real time. No refresh needed.',
    yourEndpointUrl: 'Your endpoint URL',
    listeningForEvents: 'Listening for your first event',
    noEventsYet: 'No events yet',
    docs: 'Docs',
    openFull: 'Open full',
    close: 'Close',
    noEventsFound: 'No events found',
    clearFilters: 'Clear filters',
    allEndpoints: 'All endpoints',
    filterByTime: 'Filter by time',
    rangeAll: 'All time',
    rangeAllShort: 'All',
    rangeLastHour: 'Last hour',
    rangeLast24h: 'Last 24 hours',
    rangeLast7d: 'Last 7 days',
    rangeLast30d: 'Last 30 days',
    received: 'Received',
    forwarded: 'Forwarded',
    failed: 'Failed',
    replayed: 'Replayed',
    tryAdjustingFilters: 'Try adjusting your search or filters.',

    // Endpoints page
    createFirstEndpoint: 'Create your first endpoint',
    createFirstDesc: 'Create your first endpoint to start receiving webhooks. Point your provider to the generated proxy URL.',
    targetUrl: 'Target URL',
    targetUrlOptional: 'optional',
    targetUrlHint: 'Leave empty to capture and inspect events without forwarding.',
    proxyUrl: 'Proxy URL',
    active: 'Active',
    inactive: 'Inactive',
    testingUrls: 'Testing URLs',
    useThisUrl: 'Use',
    sendTest: 'Send Test',
    sending: 'Sending...',
    slug: 'Slug',
    noRulesYet: 'No rules yet.',
    noTargetsYet: 'No additional targets yet.',

    // Event detail
    method: 'Method',
    provider: 'Provider',
    eventType: 'Event',
    source: 'Source',
    size: 'Size',
    signature: 'Signature',
    valid: 'Valid',
    invalid: 'Invalid',
    status: 'Status',
    latency: 'Latency',
    attempts: 'Attempts',
    error: 'Error',
    replaying: 'Replaying...',
    holdToReplay: 'Hold to replay',
    eventNotFound: 'Event not found',

    // Analytics
    failureRate: 'Failure Rate',
    avgLatency: 'avg {ms}ms',
    p50: 'P50 Latency',
    p95: 'P95 Latency',
    p99: 'P99 Latency',
    failedEvents: 'Failed Events',
    topErrors: 'Top Errors',
    errorMessage: 'Error Message',
    count: 'Count',
    lastSeen: 'Last Seen',
    timeline: 'Timeline',
    eventsLatencyTrends: 'Events, latency, and error trends',
    noDataForPeriod: 'No data for this period',
    noErrorsInPeriod: 'No errors in this period',
    median: 'median',
    percentile95: '95th percentile',
    percentile99: '99th percentile',
    success: 'Success',
    p95Latency: 'P95 Latency',
    volumeAndErrors: 'Volume & errors',
    stackedByStatus: 'stacked by status',
    delivered: 'Delivered',
    topEndpoints: 'Top endpoints',
    errorBreakdown: 'Error breakdown',
    noEndpointsYet: 'No endpoints yet',
    last24h: '24 hours',
    last7d: '7 days',
    last30d: '30 days',
    last90d: '90 days',

    // API Keys
    createKey: 'Create API Key',
    keyLabel: 'Label',
    keyCreated: 'Key created! Copy it now — it won\'t be shown again.',
    noKeys: 'No API keys yet',
    revokeKey: 'Revoke',
    activeKeys: 'Active Keys',
    savedItDismiss: 'I\'ve saved it, dismiss',
    deleteThisKey: 'Delete this key?',
    yesDelete: 'Yes, delete',
    deleting: 'Deleting…',
    manageAccessTokens: 'Manage access tokens for programmatic API access',

    // Settings
    workspace: 'Workspace',
    personal: 'Personal',
    personalWorkspace: 'My workspace',
    members: 'Members',
    plan: 'Plan',
    inviteMembers: 'Invite Members',
    generateInvite: 'Generate Invite Link',
    workspaceConfig: 'Workspace configuration',
    noMembersFound: 'No members found.',
    created: 'Created',
    comingSoon: 'Coming Soon',

    // Help
    helpWelcome: 'Welcome to WebhookLens',
    helpWelcomeDesc: 'Your webhook proxy & debugger',
    helpStep: 'STEP',
    helpCreateEndpoint: 'Create an Endpoint',
    helpSeeWebhooks: 'See Webhooks Live',
    helpInspectReplay: 'Inspect & Replay',
    helpApiKeys: 'API Keys',
    helpGetStarted: 'Get Started',
    // Slide body text
    helpWelcomeBody: 'WebhookLens receives webhooks on your behalf, stores them, and forwards them to your app — so you can inspect, replay, and debug every request without missing a beat.',
    helpCreateEndpointBody: 'Create an endpoint, copy its proxy URL, and paste it into your webhook provider (Stripe, GitHub, etc.).',
    helpCreateEndpointFormat: 'Proxy URL format',
    helpCreateEndpointHint: 'Find the full URL on the Endpoints page with a one-click copy button.',
    helpSeeWebhooksBody: 'The Events page updates instantly via WebSocket. No refresh needed — the green "Live" indicator means you\'re connected.',
    helpInspectReplayBody: 'Click any event to see headers, body, response, and timing. Use Replay to re-send a failed or past webhook to your target URL instantly.',
    helpInspectFullHeaders: 'Full headers & body',
    helpInspectFullHeadersDesc: 'See exactly what was sent',
    helpInspectOneClick: 'One-click replay',
    helpInspectOneClickDesc: 'Resend any event instantly',
    helpInspectTiming: 'Response timing',
    helpInspectTimingDesc: 'Latency & status codes',
    helpInspectRetry: 'Retry history',
    helpInspectRetryDesc: 'Track delivery attempts',
    helpApiKeysBody: 'Create API keys for programmatic access. Use them with the CLI or any HTTP client to query events, manage endpoints, and more.',
    helpApiKeysExample: 'Example usage',
    helpApiKeysHint: 'Find API Keys in the API Keys section of the sidebar. Keys are shown only once — copy them immediately.',
    // Advanced slide titles and body text
    helpTransforms: 'Transforms',
    helpTransformsBody: 'Modify webhook payloads before they are forwarded to your target URL.',
    helpTransformsStep1: '1. Go to endpoint detail page',
    helpTransformsStep2: '2. Click the Transforms tab',
    helpTransformsStep3: '3. Click Add Rule',
    helpTransformsStep4: '4. Choose a type:',
    helpTransformsTypes: 'set_field, remove_field, rename_field, add_header, remove_header',
    helpTransformsStep5: '5. Test with dry-run before saving',
    helpTransformsStep6: '6. Click Save — transforms apply to all future events',
    helpSmartRouting: 'Smart Routing',
    helpSmartRoutingBody: 'Route webhooks to multiple targets simultaneously with conditional rules.',
    helpSmartRoutingStep1: '1. Go to endpoint detail page',
    helpSmartRoutingStep2: '2. Click the Routing tab',
    helpSmartRoutingStep3: '3. Click Add Target',
    helpSmartRoutingStep4: '4. Set the target URL',
    helpSmartRoutingStep5: '5. Choose a condition:',
    helpSmartRoutingConditions: 'all (always), on_success, on_failure',
    helpSmartRoutingStep6: '6. Click Save — new events fan out automatically',
    helpAlerting: 'Alerting',
    helpAlertingBody: 'Get notified when something goes wrong with your webhooks.',
    helpAlertingStep1: '1. Go to the Alerts page from the sidebar',
    helpAlertingStep2: '2. Click Create Rule',
    helpAlertingStep3: '3. Choose a condition type:',
    helpAlertingConditions: 'error_rate, latency_p95, endpoint_down, volume_spike',
    helpAlertingStep4: '4. Set the threshold value',
    helpAlertingStep5: '5. Choose a channel: Slack or Webhook',
    helpAlertingStep6: '6. Click Save — alerts trigger automatically',
    helpBilling: 'Billing & Plans',
    helpBillingBody: 'Manage your subscription and upgrade your plan.',
    helpBillingStep1: '1. Go to Settings from the sidebar',
    helpBillingStep2: '2. Find the Plan section',
    helpBillingStep3: '3. Click Upgrade to Starter',
    helpBillingStep4: '4. Complete payment via Stripe checkout',
    helpBillingStep5: '5. Use Manage Subscription to access the billing portal',
    helpBillingStep6: '6. Update payment method, cancel, or switch plans anytime',
    helpBuffer: 'Buffer / Pause',
    helpBufferBody: 'Pause forwarding, modify your setup, then release buffered events.',
    helpBufferStep1: '1. On the flow view, click the Pause button',
    helpBufferStep2: '2. Incoming events are buffered (not forwarded)',
    helpBufferStep3: '3. Modify transforms or routing rules safely',
    helpBufferStep4: '4. Release events one-by-one or all at once',
    helpBufferStep5: '5. Click Resume to return to live forwarding',
    helpBufferComingSoon: 'Coming soon',
    noExpiry: 'No expiry',
    expires30d: '30 days',
    expires90d: '90 days',
    expires1y: '1 year',
    thresholdHintErrorRate: 'Percentage (e.g., 10 = 10% error rate)',
    thresholdHintLatency: 'Milliseconds (e.g., 500 = 500ms)',
    thresholdHintDown: 'Failure rate % to trigger (e.g., 90)',
    thresholdHintSpike: 'Multiplier vs average (e.g., 2.0 = 2x normal volume)',
    next: 'Next',
    back: 'Previous',
    pageOf: 'Page {page} of {total}',
    helpEventsTitle: 'Events & Monitoring',
    helpEventsDesc: 'Watch your webhook stream in real time. Filter by endpoint, status, or provider. Status badges show forwarded, failed, and replayed events at a glance.',
    helpPayloadTitle: 'Payload Inspector',
    helpPayloadDesc: 'Click any event to see the full request details. Inspect headers, body with syntax highlighting, signature validation status, and response timing.',
    helpReplayTitle: 'Replay & Retry',
    helpReplayDesc: 'One-click replay sends any event to its original target. You can also replay to a custom URL for testing. Automatic retry with backoff handles transient failures.',
    helpEndpointsTitle: 'Endpoints',
    helpEndpointsDesc: 'Create webhook endpoints and get a unique proxy URL for each. Configure the target URL where payloads are forwarded. Use httpbin URLs for quick testing.',
    helpTransformsTitle: 'Transformations & Routing',
    helpTransformsDesc: 'Transform payloads before forwarding — set, remove, or rename fields and headers. Route to multiple destinations with fan-out and conditional rules.',
    helpAnalyticsTitle: 'Analytics',
    helpAnalyticsDesc: 'Track volume, success rate, failure rate, and latency percentiles (P50, P95, P99). Timeline charts reveal traffic patterns and top errors at a glance.',
    helpAlertsTitle: 'Alerting',
    helpAlertsDesc: 'Set alert rules on error rate or latency thresholds. Notify via Slack or custom webhook. Cooldown periods prevent alert fatigue.',
    helpTeamTitle: 'Team & Security',
    helpTeamDesc: 'Invite team members and assign roles (owner, admin, member). Create API keys for programmatic access. Every action is recorded in the audit log.',
    helpSettingsTitle: 'Settings',
    helpSettingsDesc: 'Configure your workspace, set a custom domain, and choose from 3 interface languages. All settings take effect immediately.',

    // Alerts
    alerts: 'Alerts',
    alertRules: 'Alert Rules',
    newAlert: 'New Alert',
    createAlert: 'Create Alert',
    noAlerts: 'No alert rules yet',
    alertName: 'Alert Name',
    conditionType: 'Condition',
    conditionErrorRate: 'Error Rate',
    conditionLatencyP95: 'Latency P95',
    conditionEndpointDown: 'Endpoint Down',
    conditionVolumeSpike: 'Volume Spike',
    threshold: 'Threshold',
    endpointFilter: 'Endpoint (optional)',
    channelType: 'Channel Type',
    channelSlack: 'Slack',
    channelWebhook: 'Webhook',
    channelUrl: 'Channel URL',
    cooldown: 'Cooldown (minutes)',
    lastTriggered: 'Last Triggered',
    never: 'Never',
    alertHistory: 'Alert History',
    noAlertHistory: 'No alerts fired yet',
    conditionValue: 'Value',
    notifSent: 'Notified',
    enable: 'Enable',
    disable: 'Disable',
    allConditions: 'All conditions',

    // Notifications
    notifications: 'Notifications',
    markAllRead: 'Mark all as read',
    noNotifications: 'No notifications',
    justNow: 'Just now',
    agoSeconds: '{n}s ago', agoMinutes: '{n}m ago', agoHours: '{n}h ago', agoDays: '{n}d ago',
    onboarding: 'Onboarding',

    // Audit log
    audit: 'Audit Log',
    auditLog: 'Audit Log',
    noAuditLogs: 'No audit logs yet',
    allActions: 'All actions',
    exportCsv: 'Export CSV',
    user: 'User',
    action: 'Action',
    resource: 'Resource',
    ipAddress: 'IP Address',
    timestamp: 'Timestamp',

    // Invite
    inviteLink: 'Invite Link',
    inviteLinkNote: 'Share this link. New members join with member role.',
    generateInviteLink: 'Generate Invite Link',
    inviteCopied: 'Invite link copied!',

    // Custom domain

    // Endpoint transforms & routing
    advanced: 'Advanced',
    transformRules: 'Transformation Rules',
    additionalTargets: 'Additional Targets',
    addRule: 'Add Rule',
    addTarget: 'Add Target',
    ruleType: 'Type',
    rulePath: 'Path',
    ruleValue: 'Value',
    targetCondition: 'Condition',
    conditionAll: 'Always',
    conditionOnSuccess: 'On Success',
    conditionOnFailure: 'On Failure',
    transforms: 'transforms',
    targets: 'targets',
    ruleSet: 'Set',
    ruleRemove: 'Remove',
    ruleRename: 'Rename',
    ruleMapValue: 'Map Value',
    set_field: 'Set Field',
    remove_field: 'Remove Field',
    rename_field: 'Rename Field',
    add_header: 'Add Header',
    remove_header: 'Remove Header',
    ruleTarget: 'Target',
    testTransform: 'Test',
    transformResult: 'Result',
    testInputPlaceholder: '{"event": "test", "data": {"key": "value"}}',
    saveTransforms: 'Save Transforms',
    saveRoutes: 'Save Routes',
    noTransforms: 'No transform rules configured.',
    noRoutes: 'No additional routing targets.',
    extraHeaders: 'Extra Headers',
    addHeader: 'Add Header',
    endpointSettings: 'Settings',
    endpointTransforms: 'Transforms',
    endpointRouting: 'Routing',
    backToEndpoints: 'Back to Endpoints',
    saved: 'Saved',
    save: 'Save',
    saving: 'Saving...',
    headerName: 'Header Name',
    headerValue: 'Header Value',
    testInput: 'Input JSON',
    transformTesting: 'Testing...',

    // Additional UI strings
    createFirstAlert: 'Create your first alert rule to get notified',
    rule: 'rule',
    rules: 'rules',
    unread: 'unread',
    entries: 'entries',
    quickStartGuide: 'Quick start guide',

    // Billing
    planAndBilling: 'Plan & Billing',
    currentPlan: 'Current Plan',
    eventsThisMonth: 'events this month',
    upgradeToStarter: 'Upgrade to Starter — €7/mo',
    upgradeToPro: 'Upgrade to Pro — €20/mo',
    subscribeNow: 'Subscribe Now',
    manageSubscription: 'Manage Subscription',
    trialExpiresOn: 'Trial expires on',
    paymentFailedBanner: 'Payment failed. Please update your payment method.',
    paymentFailedWithDays: 'Payment failed. Please update your payment method within {days} days.',
    trialExpiringSoon: 'Your trial expires soon. Upgrade to keep your data.',
    trialDaysLeft: '{n} days left in your trial. Upgrade to keep Pro features.',
    trialOneDayLeft: 'Only 1 day left in your trial. Upgrade today.',
    trialExpiresToday: 'Your trial expires today. Upgrade to keep your data.',
    trialEnded: 'Your trial has ended. Upgrade to Starter.',
    trialWelcome: 'Welcome to WebhookLens. Your 7-day Pro trial is active — every feature unlocked.',
    whatsIncluded: "What's included",
    updateBilling: 'Update billing',
    quotaNearLimit: "You're approaching your event limit.",
    upgrade: 'Upgrade',
    dismiss: 'Dismiss',
    community: 'Community',
    planFree: 'Free',
    upgradeForMembers: 'Team members are a Pro feature. Upgrade to Pro to invite people.',
    billingUnavailable: 'Billing is temporarily unavailable. Please try again in a moment.',
    inviteFailed: 'Could not generate an invite link.',
    starter: 'Starter',
    pro: 'Pro',
    starterTrial: 'Pro Trial',
    pastDue: 'Past Due',
    suspended: 'Suspended',

    // Onboarding wizard
    onboarding_step_1_title: 'Name your workspace',
    onboarding_step_1_desc: 'Give your workspace a name so your team can recognize it.',
    onboarding_step_2_title: 'Create your first endpoint',
    onboarding_step_2_desc: 'Set up a webhook endpoint to start receiving events.',
    onboarding_step_3_title: "You're all set!",
    onboarding_step_3_desc: 'Your workspace is ready. Head to the dashboard to see incoming webhooks.',
    workspace_name: 'Workspace name',
    endpoint_name: 'Endpoint name',
    target_url: 'Target URL',
    try_urls: 'Try',
    finish: 'Go to Dashboard',
    create_another_endpoint: 'Create another endpoint',

    // Flow dashboard
    flow: 'Flow',
    flowView: 'Flow View',
    transform: 'Transform',
    target: 'Target',
    eventsPerMin: 'Events/min',
    pause: 'Pause',
    resume: 'Resume',
    paused: 'Paused', resumed: 'Resumed',
    bufferDrawerTitle: 'Buffer',
    bufferedEvents: '{count} events buffered',
    releaseOne: 'Release',
    releaseAll: 'Release All',
    discardAll: 'Discard All',
    resumeForwarding: 'Resume Forwarding',
    requeue: 'Requeue',
    released: 'Released',
    deliveryResult: 'Delivery Result',
    mainTarget: 'Main Target',
    conditionBadgeAll: 'All',
    conditionBadgeSuccess: 'On Success',
    conditionBadgeFailure: 'On Failure',
    addTransform: 'Transform',
    addRoute: 'Route',
    viewEvents: 'View Events',
    editTransforms: 'Edit Transforms',
    viewDeliveries: 'View Deliveries',
    noEndpointsFlow: 'Create an endpoint to see the flow visualization.',
    selectEndpoint: 'Select endpoint',
    favorite: 'Favorite',
    betaBannerActive: 'Beta \u2014 Free access until April 30, 2026',
    betaBannerEnded: 'Beta period ended. Upgrade to continue.',

    // GDPR / Account deletion
    myData: 'My data',
    exportDataDesc: 'Download a copy of everything we store about this workspace: account details, endpoints, members and your full event history (ZIP archive).',
    exportData: 'Download my data',
    exportingData: 'Preparing your archive...',
    exportDataError: 'Export failed. Please try again in a moment.',
    dangerZone: 'Danger Zone',
    deleteAccount: 'Delete Account',
    deleteAccountDesc: 'Permanently delete your workspace, all endpoints, events, and data. This action cannot be undone.',
    deleteAccountConfirmPrompt: 'Type DELETE to confirm. This will permanently remove your workspace, all endpoints, events, and data.',
    confirmDeleteAccount: 'Permanently Delete Account',
    deleteAccountError: 'Failed to delete account. Please try again.',

    // Signup consent
    agreeToTerms: 'I agree to the',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    and: 'and',

    // Support page
    support: 'Support',
    supportDesc: 'Get help and connect with the community',
    joinSlack: 'Join Slack',
    slackDesc: 'Get help, report bugs, request features, and chat with the team.',
    emailSupport: 'Email support',
    emailSupportDesc: 'For billing questions and account issues',
    documentation: 'Documentation',
    documentationDesc: 'Guides, API reference, and tutorials',
    slackCommunity: 'Slack Community',
    darkMode: 'Dark mode',
    lightMode: 'Light mode',

    // Flow canvas & transforms editor refonte
    livePreview: 'Live Preview',
    primaryTarget: 'Primary',
    dragHandle: 'Drag to reorder',
    invalidJson: 'Invalid JSON input',

    // i18n completeness: dashboard page strings
    eventsCount: '{count} events',
    heartbeatLast24h: 'Heartbeat · last 24h',
    heartbeatLast1h: 'Heartbeat · last 1h',
    heartbeatLast7d: 'Heartbeat · last 7d',
    heartbeatLast30d: 'Heartbeat · last 30d',
    heartbeatLast90d: 'Heartbeat · last 90d',
    now: 'now',
    eventsSuccessSuffix: 'events · {value}% success',
    eventsAtTime: 'events · {time}',
    legendSuccess: 'success',
    legendErrorHeavy: 'error-heavy',
    ruleSetField: 'Set field',
    ruleRemoveField: 'Remove field',
    ruleRenameField: 'Rename field',
    ruleAddHeader: 'Add header',
    ruleRemoveHeader: 'Remove header',
    sentStatus: 'Sent {code}',
    failedStatus: 'Failed {code}',
    testingTips: 'Testing tips',
    testingUrlsIntro: 'Use these free URLs from {provider} to test your webhooks:',
    httpbinReturns200: 'Returns 200: verifies forwarding works',
    httpbinReturns500: 'Returns 500: test error handling and retry',
    httpbinDelay: 'Responds after 5s: test timeout behavior',
    endpointsCount: '{count} endpoints',
    deleteEndpointQ: 'Delete "{name}"?',
    deleteEndpointDesc: 'This permanently removes the endpoint, its transforms and routes. Inbound webhooks to {slug} will return 404.',
    deleteEndpoint: 'Delete endpoint',
    serverError: 'Server error ({status})',
    transformTestFailed: 'Transform test failed',
    createAlertFailed: 'Failed to create alert rule.',
    alertFieldsRequired: 'Name, threshold, and channel URL are required.',
    expired: 'Expired',
    expiresIn: 'Expires {ago}',
    createdAgo: 'Created {ago}',
    lastUsedAgo: 'Last used {ago}',
    noKeysHint: 'Create one above to get started.',
    revokeKeyQ: 'Revoke "{label}"?',
    revokeKeyDesc: 'Any service or script using this key will get a 401 on the next request. This cannot be undone: revoked keys are permanently deleted, not suspended.',
    keyActive: 'active',
    keyExpired: 'expired',
    keyExpiring: 'expiring',
    unnamed: 'Unnamed',
    entriesCount: '{count} entries',
    systemActor: 'system',
    membersCount: '{count} members',
    youSuffix: '(you)',
    roleOwner: 'owner',
    roleAdmin: 'admin',
    roleMember: 'member',
    roleViewer: 'viewer',
    onboardingCreateFailed: 'Failed to create endpoint. Please check your target URL and try again.',
  },
  fr: {
    // Nav / common
    events: 'Événements', endpoints: 'Endpoints', analytics: 'Analyses',
    apiKeys: 'Clés API', settings: 'Paramètres', help: 'Aide',
    openMenu: 'Ouvrir le menu', closeMenu: 'Fermer le menu',
    signIn: 'Se connecter', signUp: 'Créer un compte', signOut: 'Déconnexion',
    email: 'E-mail', password: 'Mot de passe', name: 'Nom',
    newEndpoint: 'Nouvel endpoint', createEndpoint: 'Créer un endpoint',
    search: 'Rechercher dans le corps...', allStatuses: 'Tous les statuts',
    noWebhooks: 'Aucun webhook reçu',
    noEndpoints: 'Créez votre premier endpoint',
    replay: 'Rejouer', replaySent: 'Rejoué', replayFailed: 'Échec du rejeu', page: 'Page',
    stream: 'Stream', searchPlaceholder: 'Filtrer par id, type, corps…',
    errorsOnly: 'Erreurs uniquement', time: 'Heure', event: 'Événement', endpoint: 'Endpoint',
    streaming: 'en direct', loaded: 'chargés', errors: 'erreurs',
    firing: 'déclenchée', armed: 'armée',
    payload: 'Payload', delivery: 'Livraison',
    signatureVerified: 'Signature vérifiée', signatureInvalid: 'Signature invalide',
    replayDescription: 'Renvoyer cet événement à sa cible d\'origine.',
    holdToReplayHint: 'Maintenir · évite les renvois accidentels',
    metadata: 'Métadonnées',
    noHeaders: 'Aucun en-tête capturé.',
    noDeliveryData: 'Aucune mesure de livraison pour cet événement.',
    resizeInspector: 'Redimensionner l\'inspecteur',
    ingest: 'Réception', validate: 'Validation', forward: 'Transfert',
    totalDeliveryTime: 'Durée totale (réception → réponse) :',
    deliveryEstimateNote: 'Seule la durée totale est mesurée aujourd\'hui ; le détail par étape viendra du journal des tentatives.',
    statusCodeLabel: 'Statut de la réponse',
    responseLabel: 'Corps de la réponse',
    responseTruncated: 'tronqué à 64 Ko',
    noHttpResponse: 'Aucune réponse HTTP (erreur réseau).',
    clickToActivate: 'Activer', clickToDeactivate: 'Désactiver',
    usageUnavailable: 'Utilisation temporairement indisponible',
    delete: 'Supprimer', cancel: 'Annuler',
    retry: 'Réessayer',
    couldntLoadEvents: 'Impossible de charger le flux.',
    couldntLoadAlerts: "Impossible de charger les règles d'alerte.",
    checkConnection: 'Vérifiez votre connexion et réessayez.',
    filterByEndpoint: 'Filtrer par endpoint',
    deleteAlertQ: 'Supprimer cette alerte ?',
    deleteAlertDesc: 'Cette règle cessera la surveillance et sera supprimée. Action irréversible.',
    alertDeleteFailed: "Impossible de supprimer l'alerte.",
    alertToggleFailed: "Impossible de mettre à jour l'alerte.",
    live: 'En direct', disconnected: 'Déconnecté',
    request: 'Requête', response: 'Réponse', headers: 'En-têtes', body: 'Corps',
    totalEvents: 'Total événements', successRate: 'Taux de succès',
    copy: 'Copier', copied: 'Copié', copyProxyUrl: "Copier l'URL du proxy",
    loading: 'Chargement...',
    off: 'Hors ligne', unreadCount: '{n} non lus', language: 'Langue', navigation: 'Navigation',
    confirm: 'Confirmer', typeToConfirm: 'Tapez {x} pour confirmer.', toggleLineWrap: 'Retour à la ligne',
    done: 'Terminé',
    linkExpired: 'Lien invalide ou expiré. Veuillez vous reconnecter.',
    missingExchangeCode: 'Code d\'échange manquant',
    errorBoundaryTitle: 'Une erreur est survenue',
    errorBoundaryBody: 'La page a rencontré une erreur inattendue. Vous pouvez réessayer ou recharger l\'application.',
    reloadApp: 'Recharger l\'application',

    // Login / Signup
    signInTitle: 'Connectez-vous',
    signUpTitle: 'Créez votre compte',
    forgotPassword: 'Mot de passe oublié ?',
    forgotPasswordTitle: 'Réinitialiser votre mot de passe',
    forgotPasswordDesc: 'Saisissez votre e-mail et nous vous enverrons un lien de réinitialisation.',
    resetLinkSent: 'Si un compte existe pour cet e-mail, un lien de réinitialisation arrive.',
    sendResetLink: 'Envoyer le lien',
    resetPasswordTitle: 'Choisissez un nouveau mot de passe',
    newPassword: 'Nouveau mot de passe',
    resetPassword: 'Réinitialiser le mot de passe',
    resetSuccess: 'Mot de passe mis à jour. Vous pouvez vous connecter.',
    resetFailed: 'Ce lien est invalide ou expiré. Demandez-en un nouveau.',
    resetLinkInvalid: 'Lien de réinitialisation manquant ou invalide.',
    backToLogin: 'Retour à la connexion',
    joiningWorkspace: 'Vous avez été invité à rejoindre un espace de travail. Créez votre compte pour accepter.',
    noAccount: 'Pas de compte ?',
    hasAccount: 'Déjà un compte ?',
    invalidCredentials: 'Identifiants invalides',
    signupFailed: "Échec de l'inscription. Veuillez réessayer.",
    emailAlreadyTaken: 'Cet email est déjà associé à un compte.',
    removeMember: 'Retirer le membre',
    removeMemberConfirm: 'Cette personne perdra l’accès à cet espace de travail.',
    leaveWorkspace: 'Quitter l’espace',
    leaveWorkspaceConfirm: 'Vous perdrez l’accès à cet espace de travail jusqu’à une nouvelle invitation.',
    memberRemoveFailed: 'Impossible de retirer le membre.',
    leaveFailed: 'Impossible de quitter l’espace de travail.',
    remove: 'Retirer',
    signupValidationError: 'Vérifiez vos informations et réessayez.',
    disposableEmailBlocked: 'Les adresses email jetables ne sont pas acceptées. Merci d\'utiliser votre adresse réelle.',
    passwordMinLength: '8 caractères minimum',
    signingIn: 'Connexion...',
    creatingAccount: 'Création du compte...',
    sign_in_with_github: 'Se connecter avec GitHub',
    or: 'ou',

    // OAuth (Hydra login/consent)
    oauthConnecting: 'Connexion de votre compte…',
    oauthFailed: 'Échec de l\'autorisation. Réessayez.',
    oauthMissingChallenge: 'Requête d\'autorisation invalide.',

    // Events page
    noWebhooksTitle: 'En attente de votre premier webhook',
    noWebhooksDesc: 'Pointez n\'importe quelle source vers votre URL d\'endpoint, ou essayez tout de suite avec la commande ci-dessous.',
    sendTestCommand: 'Envoyer un événement de test',
    firstEventRealtime: 'Le premier événement arrive ici en temps réel. Aucun rafraîchissement nécessaire.',
    yourEndpointUrl: "Votre URL d'endpoint",
    listeningForEvents: 'En écoute de votre premier événement',
    noEventsYet: 'Aucun événement',
    docs: 'Docs',
    openFull: 'Plein écran',
    close: 'Fermer',
    noEventsFound: 'Aucun événement',
    clearFilters: 'Effacer les filtres',
    allEndpoints: 'Tous les endpoints',
    filterByTime: 'Filtrer par période',
    rangeAll: 'Toute la période',
    rangeAllShort: 'Tout',
    rangeLastHour: 'Dernière heure',
    rangeLast24h: 'Dernières 24 h',
    rangeLast7d: 'Derniers 7 jours',
    rangeLast30d: 'Derniers 30 jours',
    received: 'Reçu',
    forwarded: 'Transféré',
    failed: 'Échoué',
    replayed: 'Rejoué',

    // Endpoints page
    createFirstEndpoint: 'Créez votre premier endpoint',
    createFirstDesc: 'Créez un endpoint pour commencer à recevoir des webhooks.',
    targetUrl: 'URL cible',
    targetUrlOptional: 'optionnel',
    targetUrlHint: 'Laisser vide pour capturer et inspecter sans relayer.',
    proxyUrl: 'URL proxy',
    active: 'Actif',
    inactive: 'Inactif',
    testingUrls: 'URLs de test',
    useThisUrl: 'Utiliser',
    sendTest: 'Envoyer un test',
    sending: 'Envoi...',

    // Event detail
    method: 'Méthode',
    provider: 'Fournisseur',
    eventType: 'Événement',
    source: 'Source',
    size: 'Taille',
    signature: 'Signature',
    valid: 'Valide',
    invalid: 'Invalide',
    status: 'Statut',
    latency: 'Latence',
    attempts: 'Tentatives',
    error: 'Erreur',
    replaying: 'Relecture...',
    holdToReplay: 'Maintenir pour rejouer',

    // Analytics
    failureRate: 'Taux d\'échec',
    avgLatency: 'moy. {ms} ms',
    p50: 'Latence P50',
    p95: 'Latence P95',
    p99: 'Latence P99',
    failedEvents: 'Événements échoués',
    topErrors: 'Erreurs fréquentes',
    errorMessage: 'Message d\'erreur',
    count: 'Nombre',
    lastSeen: 'Dernière vue',
    timeline: 'Chronologie',

    // API Keys
    createKey: 'Créer une clé API',
    keyLabel: 'Libellé',
    keyCreated: 'Clé créée ! Copiez-la maintenant, elle ne sera plus affichée.',
    noKeys: 'Aucune clé API',
    revokeKey: 'Révoquer',

    // Settings
    workspace: 'Espace de travail',
    personal: 'Personnel',
    personalWorkspace: 'Mon espace',
    members: 'Membres',
    plan: 'Forfait',
    inviteMembers: 'Inviter des membres',
    generateInvite: 'Générer un lien d\'invitation',

    // Help
    helpWelcome: 'Bienvenue sur WebhookLens',
    helpWelcomeDesc: 'Votre proxy webhook & débogueur',
    helpStep: 'ÉTAPE',
    helpCreateEndpoint: 'Créez un endpoint',
    helpSeeWebhooks: 'Voir les webhooks en direct',
    helpInspectReplay: 'Inspecter et rejouer',
    helpApiKeys: 'Clés API',
    helpGetStarted: 'Commencer',
    helpWelcomeBody: 'WebhookLens reçoit les webhooks à votre place, les stocke et les transfère à votre application, pour que vous puissiez inspecter, rejouer et déboguer chaque requête sans rien manquer.',
    helpCreateEndpointBody: 'Créez un endpoint, copiez son URL proxy et collez-la dans votre fournisseur de webhooks (Stripe, GitHub, etc.).',
    helpCreateEndpointFormat: 'Format de l\'URL proxy',
    helpCreateEndpointHint: 'Trouvez l\'URL complète sur la page Endpoints avec un bouton de copie en un clic.',
    helpSeeWebhooksBody: 'La page Événements se met à jour instantanément via WebSocket. Pas besoin de rafraîchir, l\'indicateur vert "Live" signifie que vous êtes connecté.',
    helpInspectReplayBody: 'Cliquez sur un événement pour voir les en-têtes, le corps, la réponse et les temps de réponse. Utilisez Rejouer pour renvoyer un webhook échoué ou passé à votre URL cible.',
    helpInspectFullHeaders: 'En-têtes et corps complets',
    helpInspectFullHeadersDesc: 'Voir exactement ce qui a été envoyé',
    helpInspectOneClick: 'Rejouer en un clic',
    helpInspectOneClickDesc: 'Renvoyer n\'importe quel événement instantanément',
    helpInspectTiming: 'Temps de réponse',
    helpInspectTimingDesc: 'Latence et codes de statut',
    helpInspectRetry: 'Historique des tentatives',
    helpInspectRetryDesc: 'Suivre les tentatives de livraison',
    helpApiKeysBody: 'Créez des clés API pour un accès programmatique. Utilisez-les avec le CLI ou tout client HTTP pour interroger les événements, gérer les endpoints, et plus.',
    helpApiKeysExample: 'Exemple d\'utilisation',
    helpApiKeysHint: 'Trouvez les clés API dans la section Clés API de la barre latérale. Les clés ne sont affichées qu\'une seule fois, copiez-les immédiatement.',
    helpTransforms: 'Transformations',
    helpTransformsBody: 'Modifiez les payloads webhook avant qu\'ils ne soient transférés à votre URL cible.',
    helpTransformsStep1: '1. Allez sur la page de détail de l\'endpoint',
    helpTransformsStep2: '2. Cliquez sur l\'onglet Transformations',
    helpTransformsStep3: '3. Cliquez sur Ajouter une règle',
    helpTransformsStep4: '4. Choisissez un type :',
    helpTransformsTypes: 'set_field, remove_field, rename_field, add_header, remove_header',
    helpTransformsStep5: '5. Testez avec le dry-run avant de sauvegarder',
    helpTransformsStep6: '6. Cliquez sur Sauvegarder, les transformations s\'appliquent à tous les futurs événements',
    helpSmartRouting: 'Routage intelligent',
    helpSmartRoutingBody: 'Routez les webhooks vers plusieurs cibles simultanément avec des règles conditionnelles.',
    helpSmartRoutingStep1: '1. Allez sur la page de détail de l\'endpoint',
    helpSmartRoutingStep2: '2. Cliquez sur l\'onglet Routage',
    helpSmartRoutingStep3: '3. Cliquez sur Ajouter une cible',
    helpSmartRoutingStep4: '4. Définissez l\'URL cible',
    helpSmartRoutingStep5: '5. Choisissez une condition :',
    helpSmartRoutingConditions: 'all (toujours), on_success, on_failure',
    helpSmartRoutingStep6: '6. Cliquez sur Sauvegarder, les nouveaux événements sont distribués automatiquement',
    helpAlerting: 'Alertes',
    helpAlertingBody: 'Soyez notifié quand quelque chose ne va pas avec vos webhooks.',
    helpAlertingStep1: '1. Allez sur la page Alertes depuis la barre latérale',
    helpAlertingStep2: '2. Cliquez sur Créer une règle',
    helpAlertingStep3: '3. Choisissez un type de condition :',
    helpAlertingConditions: 'error_rate, latency_p95, endpoint_down, volume_spike',
    helpAlertingStep4: '4. Définissez la valeur du seuil',
    helpAlertingStep5: '5. Choisissez un canal : Slack ou Webhook',
    helpAlertingStep6: '6. Cliquez sur Sauvegarder, les alertes se déclenchent automatiquement',
    helpBilling: 'Facturation et plans',
    helpBillingBody: 'Gérez votre abonnement et changez de plan.',
    helpBillingStep1: '1. Allez dans les Paramètres depuis la barre latérale',
    helpBillingStep2: '2. Trouvez la section Plan',
    helpBillingStep3: '3. Cliquez sur Passer au Starter',
    helpBillingStep4: '4. Complétez le paiement via Stripe',
    helpBillingStep5: '5. Utilisez Gérer l\'abonnement pour accéder au portail de facturation',
    helpBillingStep6: '6. Mettez à jour le moyen de paiement, annulez ou changez de plan à tout moment',
    helpBuffer: 'Tampon / Pause',
    helpBufferBody: 'Mettez en pause le transfert, modifiez votre configuration, puis libérez les événements en attente.',
    helpBufferStep1: '1. Dans la vue du flux, cliquez sur le bouton Pause',
    helpBufferStep2: '2. Les événements entrants sont mis en tampon (non transférés)',
    helpBufferStep3: '3. Modifiez les transformations ou les règles de routage en toute sécurité',
    helpBufferStep4: '4. Libérez les événements un par un ou tous en même temps',
    helpBufferStep5: '5. Cliquez sur Reprendre pour revenir au transfert en direct',
    helpBufferComingSoon: 'Bientôt disponible',
    noExpiry: 'Sans expiration',
    expires30d: '30 jours',
    expires90d: '90 jours',
    expires1y: '1 an',
    thresholdHintErrorRate: 'Pourcentage (ex: 10 = 10% d\'erreurs)',
    thresholdHintLatency: 'Millisecondes (ex: 500 = 500ms)',
    thresholdHintDown: 'Taux d\'échec % pour déclencher (ex: 90)',
    thresholdHintSpike: 'Multiplicateur vs moyenne (ex: 2.0 = 2x le volume normal)',
    next: 'Suivant',
    back: 'Précédent',
    pageOf: 'Page {page} sur {total}',
    helpEventsTitle: 'Événements et surveillance',
    helpEventsDesc: 'Suivez votre flux de webhooks en temps réel. Filtrez par endpoint, statut ou fournisseur. Les badges indiquent les statuts transféré, échoué et rejoué.',
    helpPayloadTitle: 'Inspecteur de payload',
    helpPayloadDesc: 'Cliquez sur un événement pour voir tous les détails. Inspectez les en-têtes, le corps avec coloration syntaxique et la validation de signature.',
    helpReplayTitle: 'Rejouer et réessayer',
    helpReplayDesc: 'Rejouez n\'importe quel événement en un clic vers sa cible originale ou une URL personnalisée. Les tentatives automatiques gèrent les échecs transitoires.',
    helpEndpointsTitle: 'Endpoints',
    helpEndpointsDesc: 'Créez des endpoints et obtenez une URL proxy unique pour chacun. Configurez l\'URL cible et utilisez httpbin pour les tests rapides.',
    helpTransformsTitle: 'Transformations & Routage',
    helpTransformsDesc: 'Transformez les payloads avant envoi : définissez, supprimez ou renommez des champs et en-têtes. Routez vers plusieurs destinations avec des règles conditionnelles.',
    helpAnalyticsTitle: 'Analyses',
    helpAnalyticsDesc: 'Suivez le volume, taux de succès, taux d\'échec et les percentiles de latence (P50, P95, P99). Les graphiques révèlent les tendances du trafic.',
    helpAlertsTitle: 'Alertes',
    helpAlertsDesc: 'Créez des règles d\'alerte sur le taux d\'erreur ou la latence. Notifications via Slack ou webhook personnalisé. Les périodes de refroidissement évitent la fatigue des alertes.',
    helpTeamTitle: 'Équipe & Sécurité',
    helpTeamDesc: 'Invitez des membres et attribuez des rôles. Créez des clés API pour l\'accès programmatique. Chaque action est enregistrée dans le journal d\'audit.',
    helpSettingsTitle: 'Paramètres',
    helpSettingsDesc: 'Configurez votre espace de travail, définissez un domaine personnalisé et choisissez parmi 3 langues. Les paramètres prennent effet immédiatement.',

    // Alerts
    alerts: 'Alertes',
    alertRules: 'Règles d\'alerte',
    newAlert: 'Nouvelle alerte',
    createAlert: 'Créer une alerte',
    noAlerts: 'Aucune règle d\'alerte',
    alertName: 'Nom de l\'alerte',
    conditionType: 'Condition',
    conditionErrorRate: 'Taux d\'erreur',
    conditionLatencyP95: 'Latence P95',
    conditionEndpointDown: 'Endpoint indisponible',
    conditionVolumeSpike: 'Pic de volume',
    threshold: 'Seuil',
    endpointFilter: 'Endpoint (optionnel)',
    channelType: 'Canal',
    channelSlack: 'Slack',
    channelWebhook: 'Webhook',
    channelUrl: 'URL du canal',
    cooldown: 'Délai d\'attente (min)',
    lastTriggered: 'Dernier déclenchement',
    never: 'Jamais',
    alertHistory: 'Historique des alertes',
    noAlertHistory: 'Aucune alerte déclenchée',
    conditionValue: 'Valeur',
    notifSent: 'Notifié',
    enable: 'Activer',
    disable: 'Désactiver',
    allConditions: 'Toutes les conditions',

    // Notifications
    notifications: 'Notifications',
    markAllRead: 'Tout marquer comme lu',
    noNotifications: 'Aucune notification',
    justNow: 'À l\'instant',
    agoSeconds: 'il y a {n} s', agoMinutes: 'il y a {n} min', agoHours: 'il y a {n} h', agoDays: 'il y a {n} j',
    onboarding: 'Bienvenue',

    // Audit log
    audit: 'Journal d\'audit',
    auditLog: 'Journal d\'audit',
    noAuditLogs: 'Aucune entrée d\'audit',
    allActions: 'Toutes les actions',
    exportCsv: 'Exporter CSV',
    user: 'Utilisateur',
    action: 'Action',
    resource: 'Ressource',
    ipAddress: 'Adresse IP',
    timestamp: 'Horodatage',

    // Invite
    inviteLink: 'Lien d\'invitation',
    inviteLinkNote: 'Partagez ce lien. Les nouveaux membres rejoignent avec le rôle membre.',
    generateInviteLink: 'Générer un lien d\'invitation',
    inviteCopied: 'Lien d\'invitation copié !',

    // Custom domain

    // Endpoint transforms & routing
    advanced: 'Avancé',
    transformRules: 'Règles de transformation',
    additionalTargets: 'Cibles supplémentaires',
    addRule: 'Ajouter une règle',
    addTarget: 'Ajouter une cible',
    ruleType: 'Type',
    rulePath: 'Chemin',
    ruleValue: 'Valeur',
    targetCondition: 'Condition',
    conditionAll: 'Toujours',
    conditionOnSuccess: 'En cas de succès',
    conditionOnFailure: 'En cas d\'échec',
    transforms: 'transformations',
    targets: 'cibles',
    ruleSet: 'Définir',
    ruleRemove: 'Supprimer',
    ruleRename: 'Renommer',
    ruleMapValue: 'Mapper',
    set_field: 'Définir un champ',
    remove_field: 'Supprimer un champ',
    rename_field: 'Renommer un champ',
    add_header: 'Ajouter un en-tête',
    remove_header: 'Supprimer un en-tête',
    ruleTarget: 'Cible',
    testTransform: 'Tester',
    transformResult: 'Résultat',
    testInputPlaceholder: '{"event": "test", "data": {"cle": "valeur"}}',
    saveTransforms: 'Enregistrer les transformations',
    saveRoutes: 'Enregistrer les routes',
    noTransforms: 'Aucune règle de transformation configurée.',
    noRoutes: 'Aucune cible de routage supplémentaire.',
    extraHeaders: 'En-têtes supplémentaires',
    addHeader: 'Ajouter un en-tête',
    endpointSettings: 'Paramètres',
    endpointTransforms: 'Transformations',
    endpointRouting: 'Routage',
    backToEndpoints: 'Retour aux endpoints',
    saved: 'Enregistré',
    save: 'Enregistrer',
    saving: 'Enregistrement...',
    headerName: 'Nom de l\'en-tête',
    headerValue: 'Valeur de l\'en-tête',
    testInput: 'JSON d\'entrée',
    transformTesting: 'Test en cours...',

    // Additional UI strings
    createFirstAlert: 'Créez votre première règle d\'alerte pour être notifié',
    rule: 'règle',
    rules: 'règles',
    unread: 'non lues',
    entries: 'entrées',
    quickStartGuide: 'Guide de démarrage rapide',
    eventsLatencyTrends: 'Événements, latence et tendances d\'erreurs',
    noDataForPeriod: 'Aucune donnée pour cette période',
    noErrorsInPeriod: 'Aucune erreur sur cette période',
    median: 'médiane',
    percentile95: '95e percentile',
    percentile99: '99e percentile',
    success: 'Succès',
    p95Latency: 'Latence P95',
    volumeAndErrors: 'Volume & erreurs',
    stackedByStatus: 'empilé par statut',
    delivered: 'Livré',
    topEndpoints: 'Endpoints majeurs',
    errorBreakdown: 'Répartition des erreurs',
    noEndpointsYet: 'Aucun endpoint',
    last24h: '24 heures',
    last7d: '7 jours',
    last30d: '30 jours',
    last90d: '90 jours',
    activeKeys: 'Clés actives',
    savedItDismiss: 'J\'ai sauvegardé, fermer',
    deleteThisKey: 'Supprimer cette clé ?',
    yesDelete: 'Oui, supprimer',
    deleting: 'Suppression…',
    manageAccessTokens: 'Gérer les jetons d\'accès pour l\'API',
    workspaceConfig: 'Configuration de l\'espace de travail',
    noMembersFound: 'Aucun membre trouvé.',
    created: 'Créé',
    comingSoon: 'Bientôt disponible',
    eventNotFound: 'Événement introuvable',
    tryAdjustingFilters: 'Essayez d\'ajuster votre recherche ou vos filtres.',
    slug: 'Slug',
    noRulesYet: 'Aucune règle pour l\'instant.',
    noTargetsYet: 'Aucune cible supplémentaire pour l\'instant.',

    // Billing
    planAndBilling: 'Forfait et facturation',
    currentPlan: 'Forfait actuel',
    eventsThisMonth: 'événements ce mois-ci',
    upgradeToStarter: 'Passer à Starter : 7 €/mois',
    upgradeToPro: 'Passer à Pro : 20 €/mois',
    subscribeNow: 'S\'abonner maintenant',
    manageSubscription: 'Gérer l\'abonnement',
    trialExpiresOn: 'L\'essai expire le',
    paymentFailedBanner: 'Échec du paiement. Veuillez mettre à jour votre moyen de paiement.',
    paymentFailedWithDays: 'Échec du paiement. Mettez à jour votre moyen de paiement sous {days} jours.',
    trialExpiringSoon: 'Votre essai expire bientôt. Passez à un forfait payant pour conserver vos données.',
    trialDaysLeft: '{n} jours restants dans votre essai. Passez à Pro pour conserver les fonctionnalités.',
    trialOneDayLeft: 'Plus qu\'un jour dans votre essai. Passez à Pro aujourd\'hui.',
    trialExpiresToday: 'Votre essai expire aujourd\'hui. Passez à Pro pour conserver vos données.',
    trialEnded: 'Votre essai est terminé. Passez à Starter.',
    trialWelcome: 'Bienvenue sur WebhookLens. Votre essai Pro de 7 jours est actif, toutes les fonctionnalités débloquées.',
    whatsIncluded: 'Ce qui est inclus',
    updateBilling: 'Mettre à jour le paiement',
    quotaNearLimit: 'Vous approchez de votre limite d\'événements.',
    upgrade: 'Mettre à niveau',
    dismiss: 'Fermer',
    community: 'Communauté',
    planFree: 'Gratuit',
    upgradeForMembers: "Les membres d'équipe sont une fonctionnalité Pro. Passez au Pro pour inviter.",
    billingUnavailable: "Le paiement est momentanément indisponible. Veuillez réessayer dans un instant.",
    inviteFailed: "Impossible de générer un lien d'invitation.",
    starter: 'Starter',
    pro: 'Pro',
    starterTrial: 'Essai Pro',
    pastDue: 'En retard',
    suspended: 'Suspendu',

    // Onboarding wizard
    onboarding_step_1_title: 'Nommez votre espace de travail',
    onboarding_step_1_desc: 'Donnez un nom à votre espace de travail pour que votre équipe le reconnaisse.',
    onboarding_step_2_title: 'Créez votre premier endpoint',
    onboarding_step_2_desc: 'Configurez un endpoint webhook pour commencer à recevoir des événements.',
    onboarding_step_3_title: 'Tout est prêt !',
    onboarding_step_3_desc: "Votre espace de travail est prêt. Rendez-vous sur le dashboard pour voir les webhooks entrants.",
    workspace_name: "Nom de l'espace de travail",
    endpoint_name: "Nom de l'endpoint",
    target_url: 'URL cible',
    try_urls: 'Essayer',
    finish: 'Aller au dashboard',
    create_another_endpoint: 'Créer un autre endpoint',

    // Flow dashboard
    flow: 'Flux',
    flowView: 'Vue du flux',
    transform: 'Transformation',
    target: 'Cible',
    eventsPerMin: 'Événements/min',
    pause: 'Pause',
    resume: 'Reprendre',
    paused: 'En pause', resumed: 'Repris',
    bufferDrawerTitle: 'Tampon',
    bufferedEvents: '{count} \u00e9v\u00e9nements en tampon',
    releaseOne: 'Lib\u00e9rer',
    releaseAll: 'Tout lib\u00e9rer',
    discardAll: 'Tout supprimer',
    resumeForwarding: 'Reprendre le transfert',
    requeue: 'Remettre en file',
    released: 'Lib\u00e9r\u00e9',
    deliveryResult: 'R\u00e9sultat de livraison',
    mainTarget: 'Cible principale',
    conditionBadgeAll: 'Toutes',
    conditionBadgeSuccess: 'En cas de succ\u00e8s',
    conditionBadgeFailure: 'En cas d\'\u00e9chec',
    addTransform: 'Transformation',
    addRoute: 'Route',
    viewEvents: 'Voir les événements',
    editTransforms: 'Modifier les transformations',
    viewDeliveries: 'Voir les livraisons',
    noEndpointsFlow: 'Cr\u00e9ez un endpoint pour voir la visualisation du flux.',
    selectEndpoint: 'S\u00e9lectionner un endpoint',
    favorite: 'Favori',
    betaBannerActive: 'B\u00eata : Acc\u00e8s gratuit jusqu\'au 30 avril 2026',
    betaBannerEnded: 'P\u00e9riode b\u00eata termin\u00e9e. Passez \u00e0 un forfait pour continuer.',

    // GDPR / Suppression de compte
    myData: 'Mes données',
    exportDataDesc: 'Téléchargez une copie de tout ce que nous stockons pour cet espace : informations du compte, endpoints, membres et historique complet des événements (archive ZIP).',
    exportData: 'Télécharger mes données',
    exportingData: 'Préparation de votre archive...',
    exportDataError: 'Échec de l\'export. Veuillez réessayer dans un instant.',
    dangerZone: 'Zone de danger',
    deleteAccount: 'Supprimer le compte',
    deleteAccountDesc: 'Supprimez d\u00e9finitivement votre espace de travail, tous les endpoints, \u00e9v\u00e9nements et donn\u00e9es. Cette action est irr\u00e9versible.',
    deleteAccountConfirmPrompt: 'Tapez DELETE pour confirmer. Cela supprimera d\u00e9finitivement votre espace de travail, tous les endpoints, \u00e9v\u00e9nements et donn\u00e9es.',
    confirmDeleteAccount: 'Supprimer d\u00e9finitivement le compte',
    deleteAccountError: '\u00c9chec de la suppression du compte. Veuillez r\u00e9essayer.',

    // Consentement inscription
    agreeToTerms: 'J\'accepte les',
    termsOfService: 'Conditions d\'utilisation',
    privacyPolicy: 'Politique de confidentialit\u00e9',
    and: 'et',

    // Page Support
    support: 'Support',
    supportDesc: 'Obtenez de l\'aide et rejoignez la communaut\u00e9',
    joinSlack: 'Rejoindre Slack',
    slackDesc: 'Obtenez de l\'aide, signalez des bugs, demandez des fonctionnalit\u00e9s et discutez avec l\'\u00e9quipe.',
    emailSupport: 'Support par email',
    emailSupportDesc: 'Pour les questions de facturation et les probl\u00e8mes de compte',
    documentation: 'Documentation',
    documentationDesc: 'Guides, r\u00e9f\u00e9rence API et tutoriels',
    slackCommunity: 'Communaut\u00e9 Slack',
    darkMode: 'Mode sombre',
    lightMode: 'Mode clair',

    // Flow canvas & transforms editor refonte
    livePreview: 'Aperçu en direct',
    primaryTarget: 'Principal',
    dragHandle: 'Glisser pour réorganiser',
    invalidJson: 'JSON invalide',

    // i18n completeness: dashboard page strings
    eventsCount: '{count} événements',
    heartbeatLast24h: 'Activité · dernières 24 h',
    heartbeatLast1h: 'Activité · dernière heure',
    heartbeatLast7d: 'Activité · 7 derniers jours',
    heartbeatLast30d: 'Activité · 30 derniers jours',
    heartbeatLast90d: 'Activité · 90 derniers jours',
    now: 'maintenant',
    eventsSuccessSuffix: 'événements · {value}% de succès',
    eventsAtTime: 'événements · {time}',
    legendSuccess: 'succès',
    legendErrorHeavy: 'majorité d\'erreurs',
    ruleSetField: 'Définir un champ',
    ruleRemoveField: 'Supprimer un champ',
    ruleRenameField: 'Renommer un champ',
    ruleAddHeader: 'Ajouter un en-tête',
    ruleRemoveHeader: 'Supprimer un en-tête',
    sentStatus: 'Envoyé {code}',
    failedStatus: 'Échec {code}',
    testingTips: 'Conseils de test',
    testingUrlsIntro: 'Utilisez ces URLs gratuites de {provider} pour tester vos webhooks :',
    httpbinReturns200: 'Renvoie 200 : vérifie le transfert',
    httpbinReturns500: 'Renvoie 500 : teste la gestion d\'erreur et les relances',
    httpbinDelay: 'Répond après 5 s : teste le timeout',
    endpointsCount: '{count} endpoints',
    deleteEndpointQ: 'Supprimer « {name} » ?',
    deleteEndpointDesc: 'Cela supprime définitivement l\'endpoint, ses transformations et ses routes. Les webhooks entrants vers {slug} renverront 404.',
    deleteEndpoint: 'Supprimer l\'endpoint',
    serverError: 'Erreur serveur ({status})',
    transformTestFailed: 'Échec du test de transformation',
    createAlertFailed: 'Impossible de créer la règle d\'alerte.',
    alertFieldsRequired: 'Le nom, le seuil et l\'URL du canal sont requis.',
    expired: 'Expirée',
    expiresIn: 'Expire {ago}',
    createdAgo: 'Créé {ago}',
    lastUsedAgo: 'Utilisé {ago}',
    noKeysHint: 'Créez-en une ci-dessus pour commencer.',
    revokeKeyQ: 'Révoquer « {label} » ?',
    revokeKeyDesc: 'Tout service ou script utilisant cette clé recevra une erreur 401 à la prochaine requête. Action irréversible : les clés révoquées sont définitivement supprimées, pas suspendues.',
    keyActive: 'active',
    keyExpired: 'expirée',
    keyExpiring: 'expire bientôt',
    unnamed: 'Sans nom',
    entriesCount: '{count} entrées',
    systemActor: 'système',
    membersCount: '{count} membres',
    youSuffix: '(vous)',
    roleOwner: 'propriétaire',
    roleAdmin: 'admin',
    roleMember: 'membre',
    roleViewer: 'lecteur',
    onboardingCreateFailed: 'Échec de la création de l\'endpoint. Vérifiez l\'URL cible et réessayez.',
  },
  ru: {
    // Nav / common
    events: 'События', endpoints: 'Эндпоинты', analytics: 'Аналитика',
    apiKeys: 'API-ключи', settings: 'Настройки', help: 'Помощь',
    openMenu: 'Открыть меню', closeMenu: 'Закрыть меню',
    signIn: 'Войти', signUp: 'Регистрация', signOut: 'Выйти',
    email: 'Email', password: 'Пароль', name: 'Имя',
    newEndpoint: 'Новый эндпоинт', createEndpoint: 'Создать эндпоинт',
    search: 'Поиск по телу...', allStatuses: 'Все статусы',
    noWebhooks: 'Вебхуки не получены', noEndpoints: 'Создайте первый эндпоинт',
    replay: 'Повтор', replaySent: 'Повтор отправлен', replayFailed: 'Ошибка повтора', page: 'Стр.',
    stream: 'Поток', searchPlaceholder: 'Фильтр по id, типу, телу…',
    errorsOnly: 'Только ошибки', time: 'Время', event: 'Событие', endpoint: 'Эндпоинт',
    streaming: 'прямой эфир', loaded: 'загружено', errors: 'ошибок',
    firing: 'срабатывает', armed: 'активно',
    payload: 'Payload', delivery: 'Доставка',
    signatureVerified: 'Подпись подтверждена', signatureInvalid: 'Неверная подпись',
    replayDescription: 'Отправить событие повторно в исходную цель.',
    holdToReplayHint: 'Удерживайте · защита от случайной отправки',
    metadata: 'Метаданные',
    noHeaders: 'Заголовки не захвачены.',
    noDeliveryData: 'Нет данных о доставке для этого события.',
    resizeInspector: 'Изменить размер панели',
    ingest: 'Приём', validate: 'Проверка', forward: 'Отправка',
    totalDeliveryTime: 'Общее время от приёма до ответа:',
    deliveryEstimateNote: 'Сегодня измеряется только общее время; разбивка по этапам появится из журнала попыток.',
    statusCodeLabel: 'Статус ответа',
    responseLabel: 'Тело ответа',
    responseTruncated: 'обрезано до 64 КБ',
    noHttpResponse: 'Нет HTTP-ответа (сетевая ошибка).',
    clickToActivate: 'Активировать', clickToDeactivate: 'Деактивировать',
    usageUnavailable: 'Статистика использования временно недоступна',
    delete: 'Удалить', cancel: 'Отмена',
    retry: 'Повторить',
    couldntLoadEvents: 'Не удалось загрузить поток.',
    couldntLoadAlerts: 'Не удалось загрузить правила оповещений.',
    checkConnection: 'Проверьте подключение и повторите попытку.',
    filterByEndpoint: 'Фильтр по эндпоинту',
    deleteAlertQ: 'Удалить это оповещение?',
    deleteAlertDesc: 'Это правило перестанет отслеживать и будет удалено. Действие необратимо.',
    alertDeleteFailed: 'Не удалось удалить оповещение.',
    alertToggleFailed: 'Не удалось обновить оповещение.',
    live: 'Онлайн', disconnected: 'Отключено',
    request: 'Запрос', response: 'Ответ', headers: 'Заголовки', body: 'Тело',
    totalEvents: 'Всего событий', successRate: 'Успешность',
    copy: 'Копировать', copied: 'Скопировано', copyProxyUrl: 'Скопировать URL прокси', loading: 'Загрузка...',
    off: 'Не в сети', unreadCount: '{n} непрочитанных', language: 'Язык', navigation: 'Навигация',
    confirm: 'Подтвердить', typeToConfirm: 'Введите {x} для подтверждения.', toggleLineWrap: 'Перенос строк',
    done: 'Готово',
    linkExpired: 'Недействительная или просроченная ссылка. Войдите снова.',
    missingExchangeCode: 'Отсутствует код обмена',
    errorBoundaryTitle: 'Что-то пошло не так',
    errorBoundaryBody: 'На странице произошла непредвиденная ошибка. Повторите попытку или перезагрузите приложение.',
    reloadApp: 'Перезагрузить приложение',
    signInTitle: 'Вход в аккаунт', signUpTitle: 'Создать аккаунт',
    forgotPassword: 'Забыли пароль?',
    forgotPasswordTitle: 'Сброс пароля',
    forgotPasswordDesc: 'Введите e-mail, и мы отправим ссылку для сброса.',
    resetLinkSent: 'Если аккаунт с таким e-mail существует, ссылка для сброса уже в пути.',
    sendResetLink: 'Отправить ссылку',
    resetPasswordTitle: 'Выберите новый пароль',
    newPassword: 'Новый пароль',
    resetPassword: 'Сбросить пароль',
    resetSuccess: 'Пароль обновлён. Теперь вы можете войти.',
    resetFailed: 'Ссылка недействительна или истекла. Запросите новую.',
    resetLinkInvalid: 'Ссылка для сброса отсутствует или недействительна.',
    backToLogin: 'Назад ко входу',
    joiningWorkspace: 'Вас пригласили в рабочее пространство. Создайте аккаунт, чтобы принять приглашение.',
    noAccount: 'Нет аккаунта?', hasAccount: 'Уже есть аккаунт?',
    invalidCredentials: 'Неверный email или пароль',
    signupFailed: 'Не удалось создать аккаунт. Попробуйте ещё раз.',
    emailAlreadyTaken: 'Этот email уже привязан к аккаунту.',
    removeMember: 'Удалить участника',
    removeMemberConfirm: 'Участник потеряет доступ к этому рабочему пространству.',
    leaveWorkspace: 'Покинуть пространство',
    leaveWorkspaceConfirm: 'Вы потеряете доступ к этому рабочему пространству до следующего приглашения.',
    memberRemoveFailed: 'Не удалось удалить участника.',
    leaveFailed: 'Не удалось покинуть рабочее пространство.',
    remove: 'Удалить',
    signupValidationError: 'Проверьте введённые данные и попробуйте снова.',
    disposableEmailBlocked: 'Одноразовые email-адреса не поддерживаются. Укажите ваш настоящий адрес.',
    passwordMinLength: 'Минимум 8 символов',
    signingIn: 'Вход...', creatingAccount: 'Создание аккаунта...',
    sign_in_with_github: 'Войти через GitHub',
    or: 'или',

    // OAuth (Hydra login/consent)
    oauthConnecting: 'Подключение аккаунта…',
    oauthFailed: 'Ошибка авторизации. Повторите попытку.',
    oauthMissingChallenge: 'Недействительный запрос авторизации.',
    noWebhooksTitle: 'Ожидание первого вебхука',
    noWebhooksDesc: 'Направьте любой источник вебхуков на URL эндпоинта или попробуйте прямо сейчас командой ниже.',
    sendTestCommand: 'Отправить тестовое событие',
    firstEventRealtime: 'Первое событие появится здесь в реальном времени. Обновлять не нужно.',
    yourEndpointUrl: 'URL вашего эндпоинта',
    listeningForEvents: 'Ожидание первого события',
    noEventsYet: 'Событий пока нет',
    docs: 'Документация',
    openFull: 'Открыть полностью',
    close: 'Закрыть',
    noEventsFound: 'События не найдены',
    clearFilters: 'Сбросить фильтры', allEndpoints: 'Все эндпоинты',
    filterByTime: 'Фильтр по времени',
    rangeAll: 'За всё время',
    rangeAllShort: 'Все',
    rangeLastHour: 'Последний час',
    rangeLast24h: 'Последние 24 ч',
    rangeLast7d: 'Последние 7 дней',
    rangeLast30d: 'Последние 30 дней',
    received: 'Получено', forwarded: 'Переслано', failed: 'Ошибка', replayed: 'Повторено',
    createFirstEndpoint: 'Создайте первый эндпоинт',
    createFirstDesc: 'Создайте эндпоинт для получения вебхуков.',
    targetUrl: 'Целевой URL', proxyUrl: 'Прокси URL',
    targetUrlOptional: 'необязательно', targetUrlHint: 'Оставьте пустым, чтобы просматривать события без пересылки.',
    active: 'Активен', inactive: 'Неактивен', testingUrls: 'Тестовые URL',
    useThisUrl: 'Использовать', sendTest: 'Отправить тест', sending: 'Отправка...',
    method: 'Метод', provider: 'Провайдер', eventType: 'Событие',
    source: 'Источник', size: 'Размер', signature: 'Подпись',
    valid: 'Действительна', invalid: 'Недействительна', status: 'Статус',
    latency: 'Задержка', attempts: 'Попытки', error: 'Ошибка', replaying: 'Повтор...',
    holdToReplay: 'Удерживайте для повтора',
    failureRate: 'Процент ошибок', avgLatency: 'сред. {ms} мс',
    p50: 'P50', p95: 'P95', p99: 'P99', failedEvents: 'Ошибки',
    topErrors: 'Частые ошибки', errorMessage: 'Сообщение об ошибке',
    count: 'Количество', lastSeen: 'Последнее появление', timeline: 'График',
    createKey: 'Создать API-ключ', keyLabel: 'Название',
    keyCreated: 'Ключ создан! Скопируйте сейчас.', noKeys: 'Нет API-ключей',
    revokeKey: 'Отозвать', workspace: 'Рабочее пространство',
    personal: 'Личное',
    personalWorkspace: 'Моё пространство',
    members: 'Участники', plan: 'Тариф', inviteMembers: 'Пригласить',
    generateInvite: 'Создать ссылку',
    helpWelcome: 'Добро пожаловать в WebhookLens',
    helpWelcomeDesc: 'Ваш прокси и отладчик вебхуков',
    helpStep: 'ШАГ', helpCreateEndpoint: 'Создать эндпоинт',
    helpSeeWebhooks: 'Смотреть вебхуки', helpInspectReplay: 'Просмотр и повтор',
    helpApiKeys: 'API-ключи', helpGetStarted: 'Начать',
    helpWelcomeBody: 'WebhookLens принимает вебхуки за вас, сохраняет их и пересылает в ваше приложение, чтобы вы могли просматривать, повторять и отлаживать каждый запрос.',
    helpCreateEndpointBody: 'Создайте эндпоинт, скопируйте его прокси-URL и вставьте его в вашего провайдера вебхуков (Stripe, GitHub и т.д.).',
    helpCreateEndpointFormat: 'Формат прокси URL',
    helpCreateEndpointHint: 'Найдите полный URL на странице Эндпоинты с кнопкой копирования в один клик.',
    helpSeeWebhooksBody: 'Страница событий обновляется мгновенно через WebSocket. Обновление не требуется, зелёный индикатор "Live" означает, что вы подключены.',
    helpInspectReplayBody: 'Нажмите на любое событие, чтобы увидеть заголовки, тело, ответ и время отклика. Используйте Повтор для повторной отправки неудачного или прошлого вебхука на целевой URL.',
    helpInspectFullHeaders: 'Полные заголовки и тело',
    helpInspectFullHeadersDesc: 'Посмотрите, что именно было отправлено',
    helpInspectOneClick: 'Повтор в один клик',
    helpInspectOneClickDesc: 'Мгновенная повторная отправка события',
    helpInspectTiming: 'Время ответа',
    helpInspectTimingDesc: 'Задержка и коды статусов',
    helpInspectRetry: 'История повторов',
    helpInspectRetryDesc: 'Отслеживание попыток доставки',
    helpApiKeysBody: 'Создайте API-ключи для программного доступа. Используйте их с CLI или любым HTTP-клиентом для запроса событий, управления эндпоинтами и не только.',
    helpApiKeysExample: 'Пример использования',
    helpApiKeysHint: 'Найдите API-ключи в разделе API-ключи на боковой панели. Ключи показываются только один раз, скопируйте их сразу.',
    helpTransforms: 'Трансформации',
    helpTransformsBody: 'Изменяйте данные вебхуков перед пересылкой на целевой URL.',
    helpTransformsStep1: '1. Перейдите на страницу деталей эндпоинта',
    helpTransformsStep2: '2. Нажмите на вкладку Трансформации',
    helpTransformsStep3: '3. Нажмите Добавить правило',
    helpTransformsStep4: '4. Выберите тип:',
    helpTransformsTypes: 'set_field, remove_field, rename_field, add_header, remove_header',
    helpTransformsStep5: '5. Протестируйте с dry-run перед сохранением',
    helpTransformsStep6: '6. Нажмите Сохранить, трансформации применяются ко всем будущим событиям',
    helpSmartRouting: 'Умная маршрутизация',
    helpSmartRoutingBody: 'Маршрутизируйте вебхуки на несколько целей одновременно с условными правилами.',
    helpSmartRoutingStep1: '1. Перейдите на страницу деталей эндпоинта',
    helpSmartRoutingStep2: '2. Нажмите на вкладку Маршрутизация',
    helpSmartRoutingStep3: '3. Нажмите Добавить цель',
    helpSmartRoutingStep4: '4. Укажите целевой URL',
    helpSmartRoutingStep5: '5. Выберите условие:',
    helpSmartRoutingConditions: 'all (всегда), on_success, on_failure',
    helpSmartRoutingStep6: '6. Нажмите Сохранить, новые события распределяются автоматически',
    helpAlerting: 'Оповещения',
    helpAlertingBody: 'Получайте уведомления, когда что-то идёт не так с вашими вебхуками.',
    helpAlertingStep1: '1. Перейдите на страницу Оповещения на боковой панели',
    helpAlertingStep2: '2. Нажмите Создать правило',
    helpAlertingStep3: '3. Выберите тип условия:',
    helpAlertingConditions: 'error_rate, latency_p95, endpoint_down, volume_spike',
    helpAlertingStep4: '4. Установите пороговое значение',
    helpAlertingStep5: '5. Выберите канал: Slack или Webhook',
    helpAlertingStep6: '6. Нажмите Сохранить, оповещения срабатывают автоматически',
    helpBilling: 'Оплата и тарифы',
    helpBillingBody: 'Управляйте подпиской и обновляйте тариф.',
    helpBillingStep1: '1. Перейдите в Настройки на боковой панели',
    helpBillingStep2: '2. Найдите раздел Тариф',
    helpBillingStep3: '3. Нажмите Перейти на Starter',
    helpBillingStep4: '4. Завершите оплату через Stripe',
    helpBillingStep5: '5. Используйте Управление подпиской для доступа к платёжному порталу',
    helpBillingStep6: '6. Обновите способ оплаты, отмените или смените тариф в любое время',
    helpBuffer: 'Буфер / Пауза',
    helpBufferBody: 'Приостановите пересылку, измените настройки, затем отправьте буферизованные события.',
    helpBufferStep1: '1. В представлении потока нажмите кнопку Пауза',
    helpBufferStep2: '2. Входящие события буферизуются (не пересылаются)',
    helpBufferStep3: '3. Безопасно измените трансформации или правила маршрутизации',
    helpBufferStep4: '4. Отправьте события по одному или все сразу',
    helpBufferStep5: '5. Нажмите Возобновить для возврата к живой пересылке',
    helpBufferComingSoon: 'Скоро',
    noExpiry: 'Без срока', expires30d: '30 дней', expires90d: '90 дней', expires1y: '1 год',
    thresholdHintErrorRate: 'Процент (напр. 10 = 10% ошибок)',
    thresholdHintLatency: 'Миллисекунды (напр. 500 = 500мс)',
    thresholdHintDown: 'Процент ошибок для срабатывания (напр. 90)',
    thresholdHintSpike: 'Множитель к среднему (напр. 2.0 = 2x нормальный объём)',
    next: 'Далее', back: 'Назад', pageOf: 'Страница {page} из {total}',
    helpEventsTitle: 'События и мониторинг',
    helpEventsDesc: 'Наблюдайте за потоком вебхуков в реальном времени. Фильтруйте по эндпоинту, статусу или провайдеру.',
    helpPayloadTitle: 'Инспектор запросов',
    helpPayloadDesc: 'Нажмите на событие для просмотра заголовков, тела с подсветкой и статуса подписи.',
    helpReplayTitle: 'Повтор и повторные попытки',
    helpReplayDesc: 'Один клик для повтора события на исходный или произвольный URL. Автоматические повторы при сбоях.',
    helpEndpointsTitle: 'Эндпоинты',
    helpEndpointsDesc: 'Создавайте эндпоинты и получайте уникальные прокси URL. Настройте целевой URL.',
    helpTransformsTitle: 'Трансформации и маршрутизация',
    helpTransformsDesc: 'Преобразуйте запросы перед отправкой. Маршрутизируйте к нескольким получателям.',
    helpAnalyticsTitle: 'Аналитика',
    helpAnalyticsDesc: 'Отслеживайте объём, успешность и задержки (P50, P95, P99). Графики показывают паттерны.',
    helpAlertsTitle: 'Оповещения',
    helpAlertsDesc: 'Создавайте правила для ошибок или задержек. Уведомления через Slack или вебхук.',
    helpTeamTitle: 'Команда и безопасность',
    helpTeamDesc: 'Приглашайте участников, назначайте роли, создавайте API-ключи. Все действия в журнале аудита.',
    helpSettingsTitle: 'Настройки',
    helpSettingsDesc: 'Настройте рабочее пространство, домен и выберите один из 3 языков интерфейса.',
    alerts: 'Оповещения', alertRules: 'Правила', newAlert: 'Новое',
    createAlert: 'Создать', noAlerts: 'Нет правил', alertName: 'Название',
    conditionType: 'Условие', conditionErrorRate: 'Процент ошибок',
    conditionLatencyP95: 'Задержка P95', conditionEndpointDown: 'Недоступен',
    conditionVolumeSpike: 'Всплеск объёма', threshold: 'Порог',
    endpointFilter: 'Эндпоинт', channelType: 'Канал',
    channelSlack: 'Slack', channelWebhook: 'Webhook', channelUrl: 'URL канала',
    cooldown: 'Задержка (мин)', lastTriggered: 'Последнее срабатывание', never: 'Никогда',
    alertHistory: 'История', noAlertHistory: 'Нет истории',
    conditionValue: 'Значение', notifSent: 'Уведомлен',
    enable: 'Включить', disable: 'Отключить', allConditions: 'Все условия',
    notifications: 'Уведомления', markAllRead: 'Отметить все',
    noNotifications: 'Нет уведомлений', justNow: 'Только что',
    agoSeconds: '{n} с назад', agoMinutes: '{n} мин назад', agoHours: '{n} ч назад', agoDays: '{n} дн назад',
    onboarding: 'Адаптация',
    audit: 'Аудит', auditLog: 'Журнал аудита', noAuditLogs: 'Нет записей',
    allActions: 'Все действия', exportCsv: 'Экспорт CSV', user: 'Пользователь', action: 'Действие',
    resource: 'Ресурс', ipAddress: 'IP-адрес', timestamp: 'Время',
    inviteLink: 'Ссылка', inviteLinkNote: 'Поделитесь ссылкой.',
    generateInviteLink: 'Создать ссылку', inviteCopied: 'Скопировано!',
    advanced: 'Расширенные', transformRules: 'Правила трансформации',
    additionalTargets: 'Дополнительные цели', addRule: 'Добавить правило',
    addTarget: 'Добавить цель', ruleType: 'Тип', rulePath: 'Путь',
    ruleValue: 'Значение', targetCondition: 'Условие', conditionAll: 'Всегда',
    conditionOnSuccess: 'При успехе', conditionOnFailure: 'При ошибке',
    transforms: 'преобразования', targets: 'цели',
    ruleSet: 'Установить', ruleRemove: 'Удалить', ruleRename: 'Переименовать',
    ruleMapValue: 'Сопоставить',
    set_field: 'Установить поле', remove_field: 'Удалить поле', rename_field: 'Переименовать поле',
    add_header: 'Добавить заголовок', remove_header: 'Удалить заголовок',
    ruleTarget: 'Цель',
    testTransform: 'Тест',
    transformResult: 'Результат',
    testInputPlaceholder: '{"event": "test", "data": {"ключ": "значение"}}',
    saveTransforms: 'Сохранить трансформации',
    saveRoutes: 'Сохранить маршруты',
    noTransforms: 'Правила трансформации не настроены.',
    noRoutes: 'Дополнительные маршруты не настроены.',
    extraHeaders: 'Дополнительные заголовки',
    addHeader: 'Добавить заголовок',
    endpointSettings: 'Настройки',
    endpointTransforms: 'Трансформации',
    endpointRouting: 'Маршрутизация',
    backToEndpoints: 'Назад к эндпоинтам',
    saved: 'Сохранено',
    save: 'Сохранить',
    saving: 'Сохранение...',
    headerName: 'Имя заголовка',
    headerValue: 'Значение заголовка',
    testInput: 'Входной JSON',
    transformTesting: 'Тестирование...',
    createFirstAlert: 'Создайте первое правило оповещения',
    rule: 'правило', rules: 'правила', unread: 'непрочитанных', entries: 'записей',
    quickStartGuide: 'Краткое руководство',
    eventsLatencyTrends: 'События, задержка и тенденции ошибок',
    noDataForPeriod: 'Нет данных за этот период',
    noErrorsInPeriod: 'Ошибок в этом периоде нет',
    median: 'медиана', percentile95: '95-й процентиль', percentile99: '99-й процентиль',
    success: 'Успех', p95Latency: 'Задержка P95',
    volumeAndErrors: 'Объём и ошибки', stackedByStatus: 'по статусу',
    delivered: 'Доставлено',
    topEndpoints: 'Топ эндпоинтов', errorBreakdown: 'Разбивка ошибок',
    noEndpointsYet: 'Эндпоинтов нет',
    last24h: '24 часа', last7d: '7 дней', last30d: '30 дней', last90d: '90 дней',
    activeKeys: 'Активные ключи', savedItDismiss: 'Сохранено, закрыть',
    deleteThisKey: 'Удалить этот ключ?', yesDelete: 'Да, удалить', deleting: 'Удаление…',
    manageAccessTokens: 'Управление токенами доступа к API',
    workspaceConfig: 'Настройки рабочего пространства',
    noMembersFound: 'Участники не найдены.', created: 'Создано', comingSoon: 'Скоро',
    eventNotFound: 'Событие не найдено',
    tryAdjustingFilters: 'Попробуйте изменить поиск или фильтры.',
    slug: 'Slug', noRulesYet: 'Правил пока нет.', noTargetsYet: 'Дополнительных целей пока нет.',

    // Billing
    planAndBilling: 'Тариф и оплата',
    currentPlan: 'Текущий тариф',
    eventsThisMonth: 'событий в этом месяце',
    upgradeToStarter: 'Перейти на Starter : €7/мес',
    upgradeToPro: 'Перейти на Pro : €20/мес',
    subscribeNow: 'Подписаться сейчас',
    manageSubscription: 'Управление подпиской',
    trialExpiresOn: 'Пробный период заканчивается',
    paymentFailedBanner: 'Оплата не прошла. Пожалуйста, обновите способ оплаты.',
    paymentFailedWithDays: 'Оплата не прошла. Обновите способ оплаты в течение {days} дней.',
    trialExpiringSoon: 'Ваш пробный период скоро истекает. Обновите тариф, чтобы сохранить данные.',
    trialDaysLeft: 'Осталось {n} дней пробного периода. Обновите тариф, чтобы сохранить функции Pro.',
    trialOneDayLeft: 'Остался 1 день пробного периода. Обновите сегодня.',
    trialExpiresToday: 'Пробный период заканчивается сегодня. Обновите тариф, чтобы сохранить данные.',
    trialEnded: 'Ваш пробный период закончился. Перейдите на Starter.',
    trialWelcome: 'Добро пожаловать в WebhookLens. Ваш 7-дневный Pro-период активирован, все функции доступны.',
    whatsIncluded: 'Что включено',
    updateBilling: 'Обновить оплату',
    quotaNearLimit: 'Вы приближаетесь к лимиту событий.',
    upgrade: 'Обновить',
    dismiss: 'Закрыть',
    community: 'Сообщество',
    planFree: 'Бесплатно',
    upgradeForMembers: 'Участники команды доступны только на Pro. Перейдите на Pro, чтобы приглашать.',
    billingUnavailable: 'Оплата временно недоступна. Повторите попытку через мгновение.',
    inviteFailed: 'Не удалось создать ссылку-приглашение.',
    starter: 'Starter',
    pro: 'Pro',
    starterTrial: 'Пробный Pro',
    pastDue: 'Просрочено',
    suspended: 'Приостановлено',

    // Onboarding wizard
    onboarding_step_1_title: 'Назовите ваше рабочее пространство',
    onboarding_step_1_desc: 'Дайте название рабочему пространству, чтобы команда его узнавала.',
    onboarding_step_2_title: 'Создайте первый эндпоинт',
    onboarding_step_2_desc: 'Настройте эндпоинт для приёма вебхуков.',
    onboarding_step_3_title: 'Всё готово!',
    onboarding_step_3_desc: 'Ваше рабочее пространство готово. Перейдите на дашборд, чтобы увидеть входящие вебхуки.',
    workspace_name: 'Название пространства',
    endpoint_name: 'Название эндпоинта',
    target_url: 'Целевой URL',
    try_urls: 'Попробовать',
    finish: 'Перейти на дашборд',
    create_another_endpoint: 'Создать ещё эндпоинт',

    // Flow dashboard
    flow: 'Поток',
    flowView: 'Вид потока',
    transform: 'Трансформация',
    target: 'Цель',
    eventsPerMin: 'Событий/мин',
    pause: 'Пауза',
    resume: 'Возобновить',
    paused: 'На паузе', resumed: 'Возобновлено',
    bufferDrawerTitle: 'Буфер',
    bufferedEvents: '{count} событий в буфере',
    releaseOne: 'Отправить',
    releaseAll: 'Отправить все',
    discardAll: 'Удалить все',
    resumeForwarding: 'Возобновить',
    requeue: 'Вернуть в очередь',
    released: 'Отправлено',
    deliveryResult: 'Результат доставки',
    mainTarget: 'Основная цель',
    conditionBadgeAll: 'Все',
    conditionBadgeSuccess: 'При успехе',
    conditionBadgeFailure: 'При ошибке',
    addTransform: 'Трансформация',
    addRoute: 'Маршрут',
    viewEvents: 'Просмотр событий',
    editTransforms: 'Редактировать трансформации',
    viewDeliveries: 'Просмотр доставок',
    noEndpointsFlow: 'Создайте эндпоинт для визуализации потока.',
    selectEndpoint: 'Выбрать эндпоинт',
    favorite: 'Избранное',
    betaBannerActive: 'Бета : Бесплатный доступ до 30 апреля 2026',
    betaBannerEnded: 'Бета-период завершён. Обновите для продолжения.',

    // GDPR / Удаление аккаунта
    myData: 'Мои данные',
    exportDataDesc: 'Скачайте копию всего, что мы храним для этого пространства: данные аккаунта, эндпоинты, участники и полная история событий (ZIP-архив).',
    exportData: 'Скачать мои данные',
    exportingData: 'Готовим ваш архив...',
    exportDataError: 'Не удалось выполнить экспорт. Повторите попытку через мгновение.',
    dangerZone: 'Опасная зона',
    deleteAccount: 'Удалить аккаунт',
    deleteAccountDesc: 'Безвозвратно удалить ваше рабочее пространство, все эндпоинты, события и данные. Это действие нельзя отменить.',
    deleteAccountConfirmPrompt: 'Введите DELETE для подтверждения. Это безвозвратно удалит ваше рабочее пространство, все эндпоинты, события и данные.',
    confirmDeleteAccount: 'Безвозвратно удалить аккаунт',
    deleteAccountError: 'Не удалось удалить аккаунт. Попробуйте ещё раз.',

    // Согласие при регистрации
    agreeToTerms: 'Я принимаю',
    termsOfService: 'Условия использования',
    privacyPolicy: 'Политику конфиденциальности',
    and: 'и',

    // Страница поддержки
    support: 'Поддержка',
    supportDesc: 'Получите помощь и присоединяйтесь к сообществу',
    joinSlack: 'Присоединиться к Slack',
    slackDesc: 'Получите помощь, сообщите об ошибках, запросите функции и общайтесь с командой.',
    emailSupport: 'Поддержка по email',
    emailSupportDesc: 'Для вопросов по оплате и проблем с аккаунтом',
    documentation: 'Документация',
    documentationDesc: 'Руководства, справочник API и учебные материалы',
    slackCommunity: 'Сообщество Slack',
    darkMode: 'Тёмный режим',
    lightMode: 'Светлый режим',

    // Flow canvas & transforms editor refonte
    livePreview: 'Предпросмотр',
    primaryTarget: 'Основная',
    dragHandle: 'Перетащить для сортировки',
    invalidJson: 'Неверный JSON',

    // i18n completeness: dashboard page strings
    eventsCount: '{count} событий',
    heartbeatLast24h: 'Активность · за 24 ч',
    heartbeatLast1h: 'Активность · за 1 ч',
    heartbeatLast7d: 'Активность · за 7 дней',
    heartbeatLast30d: 'Активность · за 30 дней',
    heartbeatLast90d: 'Активность · за 90 дней',
    now: 'сейчас',
    eventsSuccessSuffix: 'событий · {value}% успешных',
    eventsAtTime: 'событий · {time}',
    legendSuccess: 'успешно',
    legendErrorHeavy: 'много ошибок',
    ruleSetField: 'Задать поле',
    ruleRemoveField: 'Удалить поле',
    ruleRenameField: 'Переименовать поле',
    ruleAddHeader: 'Добавить заголовок',
    ruleRemoveHeader: 'Удалить заголовок',
    sentStatus: 'Отправлено {code}',
    failedStatus: 'Ошибка {code}',
    testingTips: 'Советы по тестированию',
    testingUrlsIntro: 'Используйте эти бесплатные URL от {provider} для тестирования вебхуков:',
    httpbinReturns200: 'Возвращает 200: проверяет пересылку',
    httpbinReturns500: 'Возвращает 500: проверка обработки ошибок и повторов',
    httpbinDelay: 'Отвечает через 5 с: проверка таймаута',
    endpointsCount: '{count} эндпоинтов',
    deleteEndpointQ: 'Удалить «{name}»?',
    deleteEndpointDesc: 'Это безвозвратно удалит эндпоинт, его трансформации и маршруты. Входящие вебхуки на {slug} будут возвращать 404.',
    deleteEndpoint: 'Удалить эндпоинт',
    serverError: 'Ошибка сервера ({status})',
    transformTestFailed: 'Не удалось протестировать трансформацию',
    createAlertFailed: 'Не удалось создать правило оповещения.',
    alertFieldsRequired: 'Требуются название, порог и URL канала.',
    expired: 'Истёк',
    expiresIn: 'Истекает {ago}',
    createdAgo: 'Создан {ago}',
    lastUsedAgo: 'Использован {ago}',
    noKeysHint: 'Создайте ключ выше, чтобы начать.',
    revokeKeyQ: 'Отозвать «{label}»?',
    revokeKeyDesc: 'Любой сервис или скрипт, использующий этот ключ, получит 401 при следующем запросе. Это нельзя отменить: отозванные ключи удаляются безвозвратно, а не приостанавливаются.',
    keyActive: 'активен',
    keyExpired: 'истёк',
    keyExpiring: 'истекает',
    unnamed: 'Без названия',
    entriesCount: '{count} записей',
    systemActor: 'система',
    membersCount: '{count} участников',
    youSuffix: '(вы)',
    roleOwner: 'владелец',
    roleAdmin: 'админ',
    roleMember: 'участник',
    roleViewer: 'наблюдатель',
    onboardingCreateFailed: 'Не удалось создать эндпоинт. Проверьте целевой URL и повторите попытку.',
  },
}

function detectLang(): Lang {
  // Check localStorage first (user preference)
  const saved = localStorage.getItem('whl_lang') as Lang
  if (saved && translations[saved]) return saved

  // Detect from browser
  const browserLang = navigator.language.split('-')[0]
  const mapped: Record<string, Lang> = { en: 'en', fr: 'fr', ru: 'ru' }
  if (mapped[browserLang]) return mapped[browserLang]

  return 'en' // fallback
}

let currentLang: Lang = detectLang()

export function setLang(lang: Lang) {
  currentLang = lang
  localStorage.setItem('whl_lang', lang)
  window.dispatchEvent(new Event('langchange'))
}

export function getLang(): Lang {
  return currentLang
}

export function t(key: string, params?: Record<string, string | number>): string {
  let s = translations[currentLang]?.[key] || translations.en[key] || key
  if (params) {
    for (const k in params) {
      s = s.split('{' + k + '}').join(String(params[k]))
    }
  }
  return s
}
